import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router';
import { Form, Button, Modal, Switch, Divider, Skeleton } from 'antd';
import { isEmpty } from 'lodash';

import {
  DocumentTypePaymentType,
  UpdateDocumentTypeRequest,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import {
  useActiveDocumentType,
  useDeleteDocumentType,
  useDocumentType,
  useUpdateDocumentType,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';

import { PageProps } from '@/types/PageProps';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import AccountsDocumentTypesGeneralForm from '@/components/Financial/Accounts/DocumentTypes/General/Form';
import AccountsDocumentTypesRates from '@/components/Financial/Accounts/DocumentTypes/Rates';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { SaveButton } from '@/components/_common';
import Authorities from '@/types/Authority';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useDisclosure } from '@/hooks';
import AccountsDocumentTypesGeneralModalForm from '@/components/Financial/Accounts/DocumentTypes/General/ModalForm';
import ButtonBack from '@/components/_common/ButtonBack';
import handleFormChange from '@/hooks/handleFormChange';
import { hasPermissions } from '@/utils/functions/hasPermissions';

function DocumentTypesDetailsPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();
  const { setParamLabel } = useBreadcrumbs();
  const { documentTypeId } = useRouteMatch<{ documentTypeId: string }>().params;
  const [isActive, setIsActive] = useState<boolean>(false);
  const [activeLoading, setActiveLoading] = useState<boolean>(true);
  const [modalGeneralEnable, modalGeneralAction] = useDisclosure();
  const [formChange, setFormChange] = useState({});
  const disabled = !Object.keys(formChange).some((key) => !!key);

  const [form] = Form.useForm<UpdateDocumentTypeRequest>();

  const updateMutation = useUpdateDocumentType();
  const deleteMutation = useDeleteDocumentType();
  const activeMutation = useActiveDocumentType();

  const { data: documentType, isLoading: isLoadingDocumentTypeDetail } =
    useDocumentType(+documentTypeId);

  useEffect(() => {
    if (documentType && !isEmpty(documentType)) {
      form.setFieldsValue({ ...documentType });
      setActiveLoading(false);
      setIsActive(documentType.active);
      setParamLabel(':documentTypeId', documentType.description);
    }
  }, [documentType]);

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'documentTypes.remove' }),
      content: `${intl.formatMessage({
        id: 'documentTypes.remove.alert',
      })}`,
      onOk: async () => {
        await deleteMutation.mutateAsync(+documentTypeId);

        history.push('/app/financial/accounts/documentTypes');
      },
      okType: 'danger',
    });
  };

  const handleActiveDocumentType = async () => {
    setActiveLoading(true);
    try {
      const values = await form.validateFields();

      const isModified =
        values.initials !== documentType?.initials ||
        values.description !== documentType?.description;

      if (isModified) await handleSave();

      await activeMutation.mutateAsync(Number(documentTypeId));
      setIsActive(!isActive);
    } finally {
      setActiveLoading(false);
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();

    const obj: UpdateDocumentTypeRequest = {
      ...values,
      id: +documentTypeId,
      active: isActive,
    };

    await updateMutation.mutateAsync(obj);
  };

  const fixedRate = Form.useWatch('fixedRate', form);
  const paymentType = Form.useWatch('paymentType', form);
  const isStoneDocument = Form.useWatch('isStoneDocument', form);

  const showRates = isStoneDocument
    ? paymentType === DocumentTypePaymentType.Credit && !fixedRate
    : !fixedRate;

  return (
    <>
      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <SpaceForButton>
            <Switch
              checked={isActive}
              checkedChildren={<FormattedMessage id="commons.entity.active" />}
              unCheckedChildren={
                <FormattedMessage id="commons.entity.inactive" />
              }
              onClick={handleActiveDocumentType}
              loading={activeLoading}
              disabled={
                !hasPermissions([Authorities.REGISTER_DOCUMENT_TYPE_EDIT])
              }
            />

            <Button
              block
              danger
              onClick={handleRemoveClick}
              loading={deleteMutation.isLoading}
              disabled={
                !hasPermissions([Authorities.REGISTER_DOCUMENT_TYPE_DELETE])
              }
            >
              <FormattedMessage id="commons.form.button.remove" />
            </Button>

            <SaveButton
              block
              type="primary"
              onClick={handleSave}
              loading={updateMutation.isLoading}
              disabled={
                !hasPermissions([Authorities.REGISTER_DOCUMENT_TYPE_EDIT])
              }
            >
              <FormattedMessage id="commons.form.button.save" />
            </SaveButton>
          </SpaceForButton>
        }
      >
        <Skeleton loading={isLoadingDocumentTypeDetail}>
          <Form.Provider
            onFormChange={(name, info) =>
              handleFormChange(name, info, setFormChange)
            }
          >
            <AccountsDocumentTypesGeneralForm
              isEdit
              form={form}
              documentType={documentType}
            />

            {showRates && <AccountsDocumentTypesRates form={form} />}
          </Form.Provider>

          <Divider />

          <SpaceForButton>
            <ButtonBack
              path="/app/financial/accounts/documentTypes"
              disabled={disabled}
            />

            <Button block type="primary" onClick={modalGeneralAction.open}>
              Novo Tipo de Documento
            </Button>
          </SpaceForButton>
        </Skeleton>
      </TabContentWrapper>

      <AccountsDocumentTypesGeneralModalForm
        visible={modalGeneralEnable}
        handleVisible={modalGeneralAction.toggle}
      />
    </>
  );
}

export default DocumentTypesDetailsPage;
